import { withBasePath } from '../basePath';
import { Features, hasFeature } from '@ardoq/features';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import {
  awsUrl,
  azureUrl,
  confluenceUrl,
  excelUrl,
  jsonUrl,
  powerBiUrl,
  publicApiUrl,
  servicenow2Url,
  sharepointUrl,
  signavioExporterUrl,
  lucidUrl,
  webhookUrl,
  symbioExportUrl,
  referencesFrameworksUrl,
  visualImporterUrl,
} from '@ardoq/integrations/logos';
import { Category, Group, Integration, PRICING_MODULES } from './types';
import { unifiedIntegrations } from './unified/tileConfigs';
import { OverviewRoute } from 'integrations/common/navigation/types';
import {
  awsDictionary,
  azureDictionary,
  excelDictionary,
  jsonDictionary,
  serviceNowDictionary,
} from './common/dictionary';
import { IconName } from '@ardoq/icons';
import { APICurrentUser, Feature, OrgAccessLevel } from '@ardoq/api-types';
import {
  PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import { capitalize } from 'lodash';
import {
  FRAMEWORKS_AND_RESOURCES,
  SERVICENOW_GUIDED_EXPORTER_INTEGRATION_ID,
  VISUAL_IMPORTER_INTEGRATION_ID,
} from './common/streams/activeIntegrations/constants';

const defaultOverviewRoutes: OverviewRoute[] = [
  OverviewRoute.SCHEDULES,
  OverviewRoute.CONFIGURATIONS,
  OverviewRoute.CONNECTIONS,
];

const awsTSImporter: Integration = {
  id: 'aws-v3',
  name: awsDictionary.name,
  categories: [Category.SERVICE, Category.IMPORTER],
  description: 'Import from Aws Cloud',
  logoUrl: awsUrl,
  disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
  importerPath: 'aws-v3',
  additionalEntryPath: ['aws'],
  typescriptImporter: {
    config: {
      allowedOverviewRoutes: defaultOverviewRoutes,
    },
  },
  group: Group.INTEGRATIONS,
  requiredOrgAccessLevel: OrgAccessLevel.ADMIN,
};

const azureTSImporter: Integration = {
  id: 'azure-v3',
  name: azureDictionary.name,
  categories: [Category.SERVICE, Category.IMPORTER],
  description: `Import from ${azureDictionary.name} Cloud`,
  logoUrl: azureUrl,
  requiredFeatures: [Features.AZURE_ENABLE],
  disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
  isLegacy: true,
  importerPath: 'azure-v3',
  additionalEntryPath: ['azure'],
  typescriptImporter: {
    config: {
      allowedOverviewRoutes: defaultOverviewRoutes,
    },
  },
  group: Group.INTEGRATIONS,
  requiredOrgAccessLevel: OrgAccessLevel.ADMIN,
};

const confluenceIntegration = {
  id: 'confluence',
  name: 'Confluence',
  categories: [Category.SERVICE, Category.DOCUMENTATION],
  description: 'Integrate with Confluence',
  logoUrl: confluenceUrl,
  isEnabledWhenFeaturesUnset: true,
  helpUrl: KnowledgeBaseLink.EMBEDDING_PRESENTATIONS_INTO_CONFLUENCE,
  group: Group.GUIDES,
  requiredOrgAccessLevel: OrgAccessLevel.READER,
};

const excelTSImporter: Integration = {
  id: 'excel-v3',
  name: excelDictionary.name,
  categories: [Category.FILE, Category.IMPORTER],
  description: 'Import excel document',
  logoUrl: excelUrl,
  importerPath: 'excel-v3',
  additionalEntryPath: ['excel'],
  typescriptImporter: {
    config: {
      allowedOverviewRoutes: [OverviewRoute.CONFIGURATIONS],
    },
  },
  requiredOrgAccessLevel: OrgAccessLevel.WRITER,
  group: Group.INTEGRATIONS,
};

const jsonTSImporter: Integration = {
  id: 'json-importer',
  name: jsonDictionary.name,
  categories: [Category.FILE, Category.IMPORTER],
  description: 'Import JSON document',
  logoUrl: jsonUrl,
  importerPath: 'json-importer',
  typescriptImporter: {
    config: {
      allowedOverviewRoutes: [OverviewRoute.CONFIGURATIONS],
    },
  },
  requiredOrgAccessLevel: OrgAccessLevel.WRITER,
  group: Group.INTEGRATIONS,
};

const itPediaImporter: Integration = {
  id: 'it-pedia',
  name: 'IT-Pedia',
  categories: [Category.SERVICE, Category.IMPORTER],
  description: 'Import IT Pedia components',
  // We can't use logo folder for now since png imorts are not supported by jest
  logoUrl: withBasePath('/img/it-pedia/logo.png'),
  disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
  importerPath: 'it-pedia',
  requiredPricingModule: PRICING_MODULES.OVERSIGHT_MODULE,
  typescriptImporter: {
    config: {
      allowedOverviewRoutes: [
        OverviewRoute.SCHEDULES,
        OverviewRoute.CONNECTIONS,
      ],
    },
  },
  group: Group.INTEGRATIONS,
  requiredOrgAccessLevel: OrgAccessLevel.ADMIN,
};

const servicenowGuidedExporter: Integration = {
  id: 'servicenow-guided-exporter',
  name: 'ServiceNow Exporter',
  nameKey: 'tileName.servicenowExporter',
  categories: [Category.SERVICE, Category.EXPORTER],
  description: 'Export components and references to ServiceNow',
  logoUrl: servicenow2Url,
  disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
  requiredFeatures: [Features.SERVICENOW_GUIDED_EXPORT_ENABLE],
  importerPath: SERVICENOW_GUIDED_EXPORTER_INTEGRATION_ID,
  typescriptImporter: {},
  group: Group.INTEGRATIONS,
  requiredOrgAccessLevel: OrgAccessLevel.ADMIN,
};

const signavioExporter: Integration = {
  id: 'signavio-exporter',
  name: 'SAP Signavio Exporter',
  nameKey: 'tileName.sapSignavioExporter',
  categories: [Category.SERVICE, Category.EXPORTER],
  description: 'Export components to SAP Signavio',
  logoUrl: signavioExporterUrl,
  disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
  importerPath: 'signavio-exporter',
  typescriptImporter: {},
  group: Group.INTEGRATIONS,
  requiredOrgAccessLevel: OrgAccessLevel.ADMIN,
};

const celonisExporter: Integration = {
  id: 'celonis-exporter',
  name: 'Celonis Process Management Exporter',
  nameKey: 'tileName.celonisProcessManagementExporter',
  categories: [Category.SERVICE, Category.EXPORTER],
  description: 'Export components to Celonis',
  logoUrl: symbioExportUrl,
  disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
  importerPath: 'celonis-exporter',
  typescriptImporter: {},
  group: Group.INTEGRATIONS,
  requiredOrgAccessLevel: OrgAccessLevel.ADMIN,
};

const powerBiIntegration = {
  id: 'powerbi',
  name: 'Power BI',
  categories: [Category.SERVICE, Category.DOCUMENTATION],
  description: 'Power BI',
  logoUrl: powerBiUrl,
  disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
  helpUrl: KnowledgeBaseLink.POWER_BI_INTEGRATION,
  group: Group.GUIDES,
  requiredOrgAccessLevel: OrgAccessLevel.READER,
};

export const publicApiIntegration = {
  id: 'public-api',
  name: 'Ardoq API',
  categories: [Category.API, Category.DOCUMENTATION],
  description: 'Import data using the rest api',
  logoUrl: publicApiUrl,
  requiredFeatures: [Features.RESTAPI_ENABLE],
  disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
  typescriptImporter: {},
  importerPath: 'public-api',
  group: Group.GUIDES,
  requiredOrgAccessLevel: OrgAccessLevel.READER,
};

const servicenowTSImporter: Integration = {
  id: 'servicenow-v3',
  name: serviceNowDictionary.name,
  categories: [Category.SERVICE, Category.IMPORTER],
  description: `Import from ${serviceNowDictionary.name}`,
  logoUrl: servicenow2Url,
  importerPath: 'servicenow-v3',
  additionalEntryPath: ['servicenow-next'],
  typescriptImporter: {
    config: {
      allowedOverviewRoutes: defaultOverviewRoutes,
    },
  },
  group: Group.INTEGRATIONS,
  requiredOrgAccessLevel: OrgAccessLevel.ADMIN,
};

const sharepointIntegration = {
  id: 'sharepoint',
  name: 'Embed in SharePoint',
  nameKey: 'tileName.embedInSharepoint',
  categories: [Category.SERVICE, Category.DOCUMENTATION],
  description: 'Integrate with Sharepoint',
  logoUrl: sharepointUrl,
  isEnabledWhenFeaturesUnset: true,
  helpUrl: KnowledgeBaseLink.EMBEDDING_PRESENTATIONS_INTO_SHAREPOINT,
  group: Group.GUIDES,
  requiredOrgAccessLevel: OrgAccessLevel.READER,
};

const webhooksIntegration = {
  id: 'webhooks',
  name: 'Webhooks',
  categories: [Category.SERVICE, Category.DOCUMENTATION],
  description: 'Connect using webhooks',
  logoUrl: webhookUrl,
  requiredFeatures: [Features.WEBHOOKS_ENABLE],
  disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
  helpUrl: KnowledgeBaseLink.WEBHOOKS,
  group: Group.GUIDES,
  requiredOrgAccessLevel: OrgAccessLevel.READER,
};

const lucidGuide = {
  id: 'lucid-guide',
  name: 'Lucidchart',
  categories: [Category.SERVICE, Category.DOCUMENTATION],
  description: 'Connect using Lucidchart',
  logoUrl: lucidUrl,
  requiredFeatures: [Features.LUCID_CHART_ENABLE],
  disabledFeatures: [Features.QUICK_START_RESTRICT_AVAILABLE_INTEGRATIONS],
  helpUrl: KnowledgeBaseLink.LUCIDCHART_INTEGRATION,
  group: Group.GUIDES,
  requiredOrgAccessLevel: OrgAccessLevel.READER,
};

const frameworksAndResources = {
  id: FRAMEWORKS_AND_RESOURCES,
  name: 'Frameworks & Resources',
  nameKey: 'tileName.frameworksAndResources',
  categories: [Category.SERVICE, Category.IMPORTER],
  description: 'Imports frameworks and resources from Ardoq',
  logoUrl: referencesFrameworksUrl,
  requiredFeatures: [],
  isBeta: true,
  group: Group.PRECONFIGURED,
  requiredOrgAccessLevel: OrgAccessLevel.ADMIN,
  typescriptImporter: {},
  importerPath: 'frameworks-and-resources',
} satisfies Integration;

const visualImporter = {
  id: VISUAL_IMPORTER_INTEGRATION_ID,
  name: 'Visual Importer',
  nameKey: 'tileName.visualImporter',
  categories: [Category.SERVICE, Category.IMPORTER],
  description: 'Import from diagrams and flowcharts',
  logoUrl: visualImporterUrl,
  requiredFeatures: [Features.LUCID_IMPORT_ENABLE],
  requiredPricingModule: PRICING_MODULES.TRANSFORMATION_MODULE,
  importerPath: VISUAL_IMPORTER_INTEGRATION_ID,
  typescriptImporter: {},
  requiredOrgAccessLevel: OrgAccessLevel.WRITER,
  group: Group.PRECONFIGURED,
} satisfies Integration;

export const allIntegrations: Integration[] = [
  awsTSImporter,
  azureTSImporter,
  confluenceIntegration,
  powerBiIntegration,
  publicApiIntegration,
  sharepointIntegration,
  webhooksIntegration,
  itPediaImporter,
  excelTSImporter,
  jsonTSImporter,
  servicenowTSImporter,
  signavioExporter,
  servicenowGuidedExporter,
  lucidGuide,
  celonisExporter,
  visualImporter,
  frameworksAndResources,
  ...unifiedIntegrations,
];

const pricingModuleToDisabledHint: Record<PRICING_MODULES, string> = {
  [PRICING_MODULES.VISIBILITY_MODULE]: 'Requires Visibility Module',
  [PRICING_MODULES.TRANSFORMATION_MODULE]: 'Requires Strategic Planning Suite',
  [PRICING_MODULES.OVERSIGHT_MODULE]:
    'Requires Governance, Risk, and Compliance Suite',
};

const pricingModuleToFeature: Record<PRICING_MODULES, Feature> = {
  [PRICING_MODULES.VISIBILITY_MODULE]: Features.VISIBILITY_MODULE,
  [PRICING_MODULES.TRANSFORMATION_MODULE]: Features.TRANSFORMATION_MODULE,
  [PRICING_MODULES.OVERSIGHT_MODULE]: Features.OVERSIGHT_MODULE,
};

export const getIntegrationDisabledOptions = (
  {
    disabledFeatures,
    requiredFeatures,
    requiredOrgAccessLevel,
    requiredPrivilege,
    disableOptions,
    requiredPricingModule,
  }: Integration,
  currentUser: APICurrentUser,
  permissionContext: PermissionContext
): Integration['disableOptions'] => {
  const genericNotAvailableReason = {
    hint: 'Integration not available',
    icon: IconName.LOCK,
  };
  if (
    hasFeature(Features.USE_NEW_PRICING_MODULES) &&
    requiredPricingModule &&
    !hasFeature(pricingModuleToFeature[requiredPricingModule])
  ) {
    return {
      isDisabled: true,
      reason: {
        hint: pricingModuleToDisabledHint[requiredPricingModule],
        icon: IconName.LOCK,
      },
    };
  }

  // Integration is explicitly disabled
  if (disableOptions?.isDisabled) {
    return {
      isDisabled: true,
      reason: disableOptions.reason || genericNotAvailableReason,
    };
  }

  // Integration is disabled by feature
  if (disabledFeatures && disabledFeatures.some(hasFeature)) {
    return {
      isDisabled: true,
      reason: genericNotAvailableReason,
    };
  }

  // Integration is required by feature
  if (requiredFeatures && !requiredFeatures.every(hasFeature)) {
    return {
      isDisabled: true,
      reason: genericNotAvailableReason,
    };
  }

  // Integration require specific org access level
  if (
    !permissionsOperations.hasRequiredOrgAccessLevel(
      requiredOrgAccessLevel,
      currentUser
    )
  )
    return {
      isDisabled: true,
      reason: {
        hint: `${capitalize(requiredOrgAccessLevel)} access required to perform the action`,
        icon: IconName.SSO,
      },
    };

  // Integration requires a specific org privilege in addition to the access level
  if (
    requiredPrivilege &&
    !permissionsOperations.hasPrivilege(permissionContext, requiredPrivilege)
  )
    return {
      isDisabled: true,
      reason: {
        hint: `${capitalize(requiredPrivilege)} privilege required to perform the action`,
        icon: IconName.SSO,
      },
    };

  return { isDisabled: false };
};

export const getIntegrationByImporterPath = (integrationBasePath: string) => {
  return allIntegrations.find(
    ({ additionalEntryPath = [], importerPath }) =>
      importerPath === integrationBasePath ||
      additionalEntryPath.includes(integrationBasePath)
  );
};

export const getIntegrationById = (integrationId: string) => {
  return allIntegrations.find(({ id }) => id === integrationId);
};
